import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { goalActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { FindErrorItem, FindLoad, FindSuccessItem, renderTargetStatuses, useModal } from 'utils';
import { CreateChancel, DateCalendarForm, TimeInput } from 'components';

export const EDIT_TRANSITION_DATE_ACTION = 'EDIT_TARGET_TRANSITION_DATE';
const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';

/**
 * Edit the date + time of a target's latest status transition — the goals table's "Last Transitioned".
 * The status itself doesn't change. Date and time are required; any date is accepted (no range check, as the api's
 * `PATCH .../targets/:id/last-transition`). Picked in the browser's zone, sent as an ISO instant.
 */
export const EditTransitionDate = ({ goalId, target, requestParams }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const loading = FindLoad(EDIT_TRANSITION_DATE_ACTION);
    const success = FindSuccessItem(EDIT_TRANSITION_DATE_ACTION);
    const backendError = FindErrorItem(EDIT_TRANSITION_DATE_ACTION);

    const history = target?.statusHistory || [];
    const last = history[history.length - 1];
    const current = last?.createdAt ? moment(last.createdAt) : moment();
    // The target's current status, as the Goals table shows it (its latest change is the one being re-dated).
    const statusLabel = renderTargetStatuses.find((s) => s?.id === (target?.status || last?.newStatus))?.label;

    const { control, handleSubmit, watch } = useForm({ defaultValues: { date: current.format(DATE_FORMAT) } });
    const date = watch('date');
    const [time, setTime] = useState(current.format(TIME_FORMAT));

    // The inputs are minute-precise while stored instants have seconds: an unchanged date + time keeps the stored instant.
    const picked = useMemo(() => {
        if (!date || !time) return null;
        const chosen = moment(`${date} ${time}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
        return chosen.isSame(current, 'minute') ? moment(current) : chosen;
    }, [date, time]);
    const timeError = !time ? 'Time is required.' : '';

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_TRANSITION_DATE_ACTION));
            close();
        }
    }, [success]);

    useEffect(
        () => () => {
            dispatch(httpRequestsOnErrorsActions.removeError(EDIT_TRANSITION_DATE_ACTION));
        },
        []
    );

    const onSubmit = () => {
        if (!picked) return;
        dispatch(goalActions.editTargetTransitionDate(goalId, target?.id, picked.toISOString(), requestParams));
    };

    return (
        <div style={{ width: 420, maxWidth: '100%' }}>
            <p className="modal-header-title">Edit Last Transitioned</p>
            <p className="modal-header-sub">
                {`Change when "${target?.name}" moved to ${statusLabel || 'its current status'}. The status doesn't change.`}
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="modal-body-wrapper" style={{ gap: 16, marginTop: 16 }}>
                    <DateCalendarForm
                        name="date"
                        label="Date*"
                        control={control}
                        rules={{ required: 'Date is required.' }}
                    />
                    <TimeInput
                        label="Time*"
                        name="time"
                        defaultValue={time}
                        typeError={timeError || backendError?.error || ''}
                        onChange={(e) => setTime(e?.target?.value || null)}
                    />
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loading?.length}
                    disabled={!picked}
                    create={'Save'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                />
            </form>
        </div>
    );
};
